<template>
  <div class="security">
    <h2 class="title">Two-Factor Authentication</h2>

    <div v-if="pageState === 'loading'" class="status">Loading…</div>

    <div v-else-if="pageState === 'needAuth'" class="status">
      <p class="desc">To manage two-factor authentication, we need to verify your identity.</p>
      <div class="btn" v-on:click="authorize()" ontouchstart="">Continue</div>
    </div>

    <div v-else-if="pageState === 'error'" class="status error">
      {{ errorMessage }}
      <div class="btn" v-on:click="authorize()" ontouchstart="">Try again</div>
    </div>

    <div v-else class="content">
      <p class="desc">Use an authenticator app (Google Authenticator, Authy, 1Password, etc.) to generate verification codes.</p>

      <div class="list">
        <div v-for="auth in authenticators" v-bind:key="auth.id" class="item">
          <div class="item-name">{{ auth.name }}</div>
          <div class="btn danger" v-on:click="remove(auth.id)" ontouchstart="">Remove</div>
        </div>
      </div>

      <div v-if="!pendingEnrollment && authenticators.length === 0" class="enroll">
        <div class="status">Preparing…</div>
      </div>

      <div v-if="pendingEnrollment" class="enroll-flow">
        <p class="step">1. Scan this QR code with your authenticator app.</p>
        <img v-if="qrDataUrl" v-bind:src="qrDataUrl" class="qr" alt="QR code">
        <p class="secret">Or enter this key manually:<br>
          <code class="secret-key" v-on:click="copySecret()" ontouchstart="" title="Click to copy">{{ pendingEnrollment.secret }}</code>
          <transition name="fade"><span v-if="secretCopied" class="copy-hint">Copied!</span></transition>
        </p>

        <div v-if="pendingEnrollment.recoveryCodes.length" class="recovery">
          <p class="warn">Save these recovery codes now — they are shown only once.</p>
          <ul>
            <li v-for="(code, i) in pendingEnrollment.recoveryCodes" v-bind:key="i">{{ code }}</li>
          </ul>
        </div>

        <p class="step">2. Enter the 6-digit code from your app.</p>
        <comp-otp-input v-model="otp" v-bind:length="6" v-on:complete="onOtpComplete()"></comp-otp-input>
        <div v-if="enrollError" class="inline-error">{{ enrollError }}</div>
        <div class="actions">
          <div class="btn" v-bind:class="{ disabled: verifying }" v-on:click="submitOtp()" ontouchstart="">{{ verifying ? 'Verifying…' : 'Verify & activate' }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import QRCode from 'qrcode'
import CompOtpInput from '@/components/CompOtpInput'
import { mapMfaError } from '@/utils/mfaHelpers'

export default {
  name: 'security',
  components: {
    CompOtpInput
  },
  data: function () {
    return {
      pageState: 'loading', // loading | needAuth | ready | error
      errorMessage: '',
      otp: '',
      enrollError: '',
      qrDataUrl: '',
      secretCopied: false,
      verifying: false
    }
  },
  computed: {
    authenticators: function () { return this.$store.getters.mfaAuthenticators },
    pendingEnrollment: function () { return this.$store.getters.mfaPendingEnrollment }
  },
  created: function () {
    if (window.location.hash.indexOf('access_token=') !== -1) {
      // Returning from the authorize() redirect with an MFA-scoped token in the hash.
      this.resume()
    } else if (this.$store.getters.hasMfaToken) {
      this.loadAndReady()
    } else {
      this.trySilent()
    }
  },
  beforeDestroy: function () {
    this.$store.dispatch('clearMfaSession')
  },
  methods: {
    trySilent: function () {
      // Attempt to get the MFA token without a redirect. Falls back to the
      // Continue button (interactive authorize) when Auth0 needs an MFA step-up
      // or the SSO cookie is unavailable (e.g. third-party cookies on localhost).
      let self = this
      this.pageState = 'loading'
      this.$store.dispatch('initMfaSession').then(function () {
        return self.$store.dispatch('loadMfaAuthenticators')
      }).then(function () {
        self.pageState = 'ready'
        self.maybeAutoEnroll()
      }).catch(function () {
        self.pageState = 'needAuth'
      })
    },
    authorize: function () {
      // Full-page redirect to Auth0; returns to this page with the token in the hash.
      this.$store.dispatch('startMfaAuthorize')
    },
    resume: function () {
      let self = this
      this.pageState = 'loading'
      this.$store.dispatch('resumeMfaSession').then(function () {
        // Strip the token from the URL so it is not left in history.
        window.history.replaceState({}, document.title, window.location.pathname + window.location.search)
        return self.$store.dispatch('loadMfaAuthenticators')
      }).then(function () {
        self.pageState = 'ready'
        self.maybeAutoEnroll()
      }).catch(function (err) {
        self.errorMessage = mapMfaError(err)
        self.pageState = 'error'
      })
    },
    loadAndReady: function () {
      let self = this
      this.pageState = 'loading'
      this.$store.dispatch('loadMfaAuthenticators').then(function () {
        self.pageState = 'ready'
        self.maybeAutoEnroll()
      }).catch(function (err) {
        self.errorMessage = mapMfaError(err)
        self.pageState = 'error'
      })
    },
    maybeAutoEnroll: function () {
      // No authenticator yet → go straight to the QR enrollment, no extra click.
      if (this.authenticators.length === 0 && !this.pendingEnrollment) {
        this.startEnrollment()
      }
    },
    startEnrollment: function () {
      // Only one authenticator app is allowed; ignore if one already exists.
      if (this.authenticators.length >= 1) return
      let self = this
      this.enrollError = ''
      this.otp = ''
      this.qrDataUrl = ''
      this.$store.dispatch('startMfaEnrollment').then(function () {
        let uri = self.pendingEnrollment && self.pendingEnrollment.barcodeUri
        if (uri) {
          QRCode.toDataURL(uri).then(function (url) { self.qrDataUrl = url })
        }
      }).catch(function (err) {
        self.enrollError = mapMfaError(err)
      })
    },
    onOtpComplete: function () {
      // Auto-submit when all 6 digits are entered, with a short beat so the
      // transition does not feel abrupt (a "Verifying…" state is shown).
      if (this.verifying) return
      this.verifying = true
      this.enrollError = ''
      let self = this
      setTimeout(function () { self.doConfirm() }, 500)
    },
    submitOtp: function () {
      if (this.verifying) return
      this.verifying = true
      this.enrollError = ''
      this.doConfirm()
    },
    doConfirm: function () {
      let self = this
      this.$store.dispatch('confirmMfaEnrollment', { otp: this.otp }).then(function () {
        self.otp = ''
        self.qrDataUrl = ''
        self.verifying = false
      }).catch(function (err) {
        self.verifying = false
        self.enrollError = mapMfaError(err)
      })
    },
    copySecret: function () {
      let self = this
      let secret = this.pendingEnrollment && this.pendingEnrollment.secret
      if (!secret) return
      let flag = function () {
        self.secretCopied = true
        setTimeout(function () { self.secretCopied = false }, 1500)
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(secret).then(flag, function () {
          self.fallbackCopy(secret)
          flag()
        })
      } else {
        this.fallbackCopy(secret)
        flag()
      }
    },
    fallbackCopy: function (text) {
      let ta = document.createElement('textarea')
      ta.value = text
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.focus()
      ta.select()
      try { document.execCommand('copy') } catch (e) { /* ignore */ }
      document.body.removeChild(ta)
    },
    remove: function (id) {
      let self = this
      if (!window.confirm('Remove this authenticator app?')) return
      this.$store.dispatch('removeMfaAuthenticator', { id: id }).then(function () {
        // Now at zero authenticators → go straight back to QR enrollment.
        self.maybeAutoEnroll()
      }).catch(function (err) {
        self.errorMessage = mapMfaError(err)
        self.pageState = 'error'
      })
    }
  }
}
</script>

<style scoped>
.security { color: var(--gray-high); max-width: 520px; margin: 0 auto; text-align: center; }
.security .title { font-size: 20px; font-weight: bold; margin-bottom: 1em; }
.security .desc { color: var(--gray-medium); margin-bottom: 1.5em; }
.security .status { padding: 1em 0; }
.security .status.error { color: var(--danger); }
.security .list { margin-bottom: 1.5em; }
.security .item { display: flex; flex-direction: row; justify-content: space-between; align-items: center; padding: 0.75em 0; border-bottom: 1px solid var(--border); }
.security .btn { display: inline-block; cursor: pointer; padding: 0.6em 1em; background-color: var(--primary); color: #fff; font-weight: bold; text-transform: uppercase; font-size: 12px; border-radius: 4px; }
.security .btn.disabled { opacity: 0.6; pointer-events: none; }
.security .btn.ghost { background-color: transparent; border: 1px solid var(--border); color: var(--gray-high); }
.security .btn.danger { background-color: var(--danger); }
.security .qr { width: 180px; height: 180px; background: #fff; padding: 8px; margin: 0.5em auto; display: block; border: 1px solid var(--border); }
.security .secret { line-height: 1.9; }
.security .secret-key { background: var(--blue-gray-medium); color: var(--gray-high); padding: 0.25em 0.5em; border-radius: 4px; cursor: pointer; user-select: all; letter-spacing: 0.05em; transition: background-color .15s; }
.security .secret-key:hover { background: var(--blue-gray-high); color: #fff; }
.security .copy-hint { font-size: 11px; color: var(--gray-medium); margin-left: 0.5em; }
.security .recovery .warn { color: var(--warning-shade); font-weight: bold; }
.security .recovery ul { columns: 2; text-align: left; display: inline-block; padding-left: 1.2em; }
.security .inline-error { color: var(--danger); margin: 0.5em 0; }
.security .actions { margin-top: 1em; display: flex; justify-content: center; }
.security .actions .btn { margin: 0 0.25em; }
.security .step { margin: 1em 0 0.5em; font-weight: bold; }
</style>



// WEBPACK FOOTER //
// src/pages/Account/Security.vue